import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, ChevronDown, FileText, FolderOpen, Plus } from "lucide-react";
import { isBookCollectionKey, knowledgeBaseLabel } from "@/lib/knowledgeBaseRegistry";
import { suggestBookCollectionKey } from "@/lib/knowledgeBookRegistry";
import { bookPath } from "@/lib/knowledgeModel";
import {
  knowledgeArticlesPath,
  knowledgeBasePath,
  knowledgeLibrariesPath,
} from "@/lib/knowledgePaths";
import {
  KnowledgeChrome,
  KnowledgeError,
  KnowledgeNeedsBase,
  knowledgePrimaryLinkClass,
  knowledgeSecondaryLinkClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useKnowledgeArticles } from "@/pages/knowledge/useKnowledgeArticles";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

const fieldClass =
  "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-slate-900";

export function KnowledgeBooksPage() {
  const navigate = useNavigate();
  const { books: customBooks, createBook } = useCustomBooks();
  const { articles, loading, error } = useKnowledgeArticles();
  const { active, hasBases, activeId } = useKnowledgeBases();

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keyDraft, setKeyDraft] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMoreOpen(false);
    }
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const customByKey = useMemo(() => {
    const map = new Map<string, (typeof customBooks)[number]>();
    for (const book of customBooks) map.set(book.collectionKey, book);
    return map;
  }, [customBooks]);

  const counts = new Map<string, number>();
  for (const article of articles) {
    for (const key of article.collectionKeys) {
      if (!isBookCollectionKey(key)) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }

  const bookKeys = new Set<string>();
  for (const key of counts.keys()) bookKeys.add(key);
  for (const book of customBooks) bookKeys.add(book.collectionKey);

  const rows = [...bookKeys].sort().map((key) => {
    const custom = customByKey.get(key);
    return {
      key,
      title: custom?.title || key,
      description:
        custom?.description?.trim() ||
        (custom
          ? "Book you created. Add articles from the book page; membership is stored as a collection key."
          : "Stored as a collection key on the article."),
      count: counts.get(key) || 0,
    };
  });

  function openForm() {
    setShowForm(true);
    setFormError(null);
    setTitle("");
    setDescription("");
    setKeyDraft("");
    setKeyTouched(false);
  }

  function onTitleChange(value: string) {
    setTitle(value);
    if (!keyTouched) setKeyDraft(suggestBookCollectionKey(value));
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const result = await createBook({
      title,
      description: description.trim() || undefined,
      collectionKey: keyDraft.trim() || undefined,
    });
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    setShowForm(false);
    void navigate(bookPath(activeId, result.book.collectionKey));
  }

  if (!hasBases) {
    return (
      <KnowledgeChrome
        title="Books"
        description="Optional collection keys inside a knowledge base. Create a knowledge base first."
        badge="Optional"
        showBaseSwitcher={false}
      >
        <KnowledgeNeedsBase />
      </KnowledgeChrome>
    );
  }

  const moreMenu = (
    <div ref={moreRef} className="relative">
      <button
        type="button"
        onClick={() => setMoreOpen((value) => !value)}
        className={knowledgeSecondaryLinkClass}
        aria-label="More knowledge links"
        aria-haspopup="menu"
        aria-expanded={moreOpen}
      >
        More
        <ChevronDown className="h-4 w-4" aria-hidden />
      </button>
      {moreOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-48 origin-top-right rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-slate-900"
        >
          <Link
            role="menuitem"
            to={knowledgeBasePath(activeId)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800"
            onClick={() => setMoreOpen(false)}
          >
            <BookOpen className="h-4 w-4" aria-hidden />
            Knowledge base
          </Link>
          <Link
            role="menuitem"
            to={knowledgeLibrariesPath(activeId)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800"
            onClick={() => setMoreOpen(false)}
          >
            <FolderOpen className="h-4 w-4" aria-hidden />
            Libraries
          </Link>
          <Link
            role="menuitem"
            to={knowledgeArticlesPath(activeId)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800"
            onClick={() => setMoreOpen(false)}
          >
            <FileText className="h-4 w-4" aria-hidden />
            Articles
          </Link>
        </div>
      ) : null}
    </div>
  );

  return (
    <KnowledgeChrome
      title="Books"
      description={`Optional collection keys inside "${active ? knowledgeBaseLabel(active) : "this knowledge base"}". Articles can stand alone with no book. Nothing is pre-created; add a book when you need one.`}
      badge="Optional"
      actions={
        <>
          {moreMenu}
          <button type="button" onClick={openForm} className={knowledgePrimaryLinkClass}>
            <Plus className="h-4 w-4" aria-hidden />
            New book
          </button>
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {formError && !showForm ? <KnowledgeError message={formError} /> : null}

      {showForm ? (
        <form
          onSubmit={onCreate}
          className="max-w-lg space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-950/40"
        >
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">New book</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Creates a book in this browser for your org. You can add articles on the next screen.
              No new server API is used.
            </p>
          </div>
          {formError ? <KnowledgeError message={formError} /> : null}
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Title
            <input
              type="text"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              required
              autoFocus
              placeholder="Product handbook"
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Description <span className="font-normal text-slate-500">(optional)</span>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={2}
              placeholder="Short purpose shown under the title"
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Key <span className="font-normal text-slate-500">(optional slug)</span>
            <input
              type="text"
              value={keyDraft}
              onChange={(event) => {
                setKeyTouched(true);
                setKeyDraft(event.target.value);
              }}
              placeholder={suggestBookCollectionKey(title || "book")}
              className={`${fieldClass} font-mono text-xs`}
            />
          </label>
          <p className="text-xs text-slate-500">
            The key is what articles store in collectionKeys. Leave blank to derive it from the
            title.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={!title.trim()}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              Create book
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setFormError(null);
              }}
              className={knowledgeSecondaryLinkClass}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {loading ? <p className="text-sm text-slate-500">Loading books…</p> : null}
      {!loading && !showForm && rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-12 text-center dark:border-white/20 dark:bg-slate-950/40">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">0 books</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600 dark:text-slate-400">
            Nothing is created for you automatically. Add a book when you want to group articles
            under a collection key.
          </p>
          <button
            type="button"
            onClick={openForm}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            <Plus className="h-4 w-4" aria-hidden />
            New book
          </button>
        </div>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((row) => (
          <article
            key={row.key}
            className="flex flex-col rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-950/40"
          >
            <div className="flex items-start justify-between gap-2">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                <BookOpen className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                {row.title}
              </h2>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-white/10 dark:text-slate-300">
                {row.count.toLocaleString()} {row.count === 1 ? "article" : "articles"}
              </span>
            </div>
            <p className="mt-1 font-mono text-[11px] text-slate-500">{row.key}</p>
            <p className="mt-2 flex-1 text-sm text-slate-600 dark:text-slate-400">{row.description}</p>
            <Link
              to={bookPath(activeId, row.key)}
              className="mt-4 inline-flex w-fit rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
            >
              Open book
            </Link>
          </article>
        ))}
      </div>
    </KnowledgeChrome>
  );
}
